// Admin v2 — Játékos profil (Figma ADM_02e–02h): fejléc-kártya + Statisztikák / Mérkőzés történet /
// Eredmények / Barátok / Üzemeltetés / Beállítások fülek, a mobilapp kollekcióiból élőben.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, limit, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '../icons';
import {
  AdminUser, fmtDateDot, fmtInt, isActive, lastActiveText, normalizeUser, ROLE_LABEL, ROLE_TONE, roleOf, toDate,
} from '../data';
import {
  ActionMenu, Avatar, Badge, Button, Card, Empty, IconTile, PageHeader, SectionTitle, Segmented, Spinner, StatCard, Tabs, Toggle, Tone, useToast,
} from '../ui';
import { Dialog, UserDialogs } from './userActions';

type Tab = 'stats' | 'matches' | 'achievements' | 'friends' | 'operation' | 'settings';

/* ---------- jelvények (a mobilapp services/achievements.ts BADGES listája szerint) ---------- */
type Cat = 'wins' | 'matches' | 'skill' | 'other';
type Tier = 'bronz' | 'ezüst' | 'arany' | 'legendás';
const BADGES: { id: string; title: string; desc: string; tier: Tier; cat: Cat; icon: string }[] = [
  { id: 'first_win', title: 'Első vér', desc: 'Nyerd meg az első meccsedet.', tier: 'bronz', cat: 'wins', icon: 'Trophy' },
  { id: 'wins_10', title: 'Sorozatgyőztes', desc: 'Nyerj 10 meccset.', tier: 'ezüst', cat: 'wins', icon: 'Trophy' },
  { id: 'wins_50', title: 'Veterán', desc: 'Nyerj 50 meccset.', tier: 'arany', cat: 'wins', icon: 'Medal' },
  { id: 'matches_50', title: 'Törzsvendég', desc: 'Játssz le 50 meccset.', tier: 'ezüst', cat: 'matches', icon: 'Clock' },
  { id: 'ppr_60', title: 'Hatvanas klub', desc: 'Érj el 60+ átlagot egy meccsen.', tier: 'bronz', cat: 'skill', icon: 'Target' },
  { id: 'ppr_80', title: 'Nyolcvanas klub', desc: 'Érj el 80+ átlagot egy meccsen.', tier: 'arany', cat: 'skill', icon: 'Target' },
  { id: 'club_180', title: '180 Király', desc: 'Dobj 180-at egy meccsen.', tier: 'legendás', cat: 'skill', icon: 'Lightning' },
  { id: 'cp_2', title: 'CP2 szint', desc: 'Érd el a CP2 versenyszintet.', tier: 'bronz', cat: 'other', icon: 'ShieldStar' },
  { id: 'cp_3', title: 'CP3 szint', desc: 'Érd el a CP3 versenyszintet.', tier: 'ezüst', cat: 'other', icon: 'ShieldStar' },
  { id: 'cp_4', title: 'CP4 szint', desc: 'Érd el a CP4 versenyszintet.', tier: 'arany', cat: 'other', icon: 'ShieldStar' },
  { id: 'cp_5', title: 'CP5 szint', desc: 'Érd el a CP5 versenyszintet.', tier: 'legendás', cat: 'other', icon: 'ShieldStar' },
  { id: 'trainer_10', title: 'Szorgalmas', desc: 'Fejezz be 10 edzést.', tier: 'bronz', cat: 'other', icon: 'GraduationCap' },
];
const TIER_TONE: Record<Tier, Tone> = { bronz: 'green', 'ezüst': 'cyan', arany: 'gold', 'legendás': 'purple' };
const TIER_RANK: Record<Tier, number> = { bronz: 0, 'ezüst': 1, arany: 2, 'legendás': 3 };

interface MatchRow { id: string; opponent: string; opponentSub: string; result: 'win' | 'loss' | 'draw'; score: string; mode: string; venue: string; date: Date | null }
export interface XpRow { id: string; label: string; amount: number; at: Date | null }
interface FriendRow { key: string; uid: string; name: string; sub: string; avatar: string | null; docId: string; coll: 'friendships' | 'friend_requests' }

export default function PlayerProfilePage() {
  const { uid = '' } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const { user: me } = useAuth();
  const [u, setU] = useState<AdminUser | null | undefined>(undefined);
  const [tab, setTab] = useState<Tab>('stats');
  const [dialog, setDialog] = useState<Dialog>(null);

  const [matches, setMatches] = useState<MatchRow[] | null>(null);
  const [xp, setXp] = useState<XpRow[] | null>(null);
  const [earned, setEarned] = useState<Record<string, Date | null> | null>(null);
  const [friends, setFriends] = useState<{ friends: FriendRow[]; incoming: FriendRow[]; outgoing: FriendRow[] } | null>(null);
  const [venueNames, setVenueNames] = useState<Record<string, string>>({});

  useEffect(() => onSnapshot(doc(db, 'users', uid), (s) => setU(s.exists() ? normalizeUser(s.id, s.data()) : null), () => setU(null)), [uid]);

  // mérkőzések + XP — a Statisztikák fülhöz is kell
  useEffect(() => {
    getDocs(query(collection(db, 'match_history'), where('userId', '==', uid), limit(300)))
      .then((s) => setMatches(s.docs.map((d) => {
        const v = d.data();
        const opp = String(v.opponent ?? 'Ismeretlen');
        const cp = typeof v.opponentCp === 'number' ? v.opponentCp : typeof v.opponentCP === 'number' ? v.opponentCP : null;
        const res = v.result === 'win' || v.result === 'loss' || v.result === 'draw' ? v.result : 'loss';
        return {
          id: d.id, opponent: opp, opponentSub: cp !== null ? `CP ${cp}` : /robbi/i.test(opp) ? 'Gép' : '',
          result: res, score: String(v.score ?? '—').replace('-', ' – '), mode: String(v.gameMode ?? '—') + (v.format ? ` · ${v.format}` : ''),
          venue: String(v.venue ?? v.venueName ?? '') || '—', date: toDate(v.date ?? v.createdAt),
        } as MatchRow;
      }).sort((a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0))))
      .catch(() => setMatches([]));
    getDocs(query(collection(db, 'xp_transactions'), where('userId', '==', uid), limit(200)))
      .then((s) => setXp(s.docs.map((d) => {
        const v = d.data();
        return { id: d.id, label: String(v.description ?? v.reason ?? v.label ?? 'XP'), amount: Number(v.amount ?? 0), at: toDate(v.createdAt ?? v.timestamp) };
      }).sort((a, b) => (b.at?.getTime() ?? 0) - (a.at?.getTime() ?? 0))))
      .catch(() => setXp([]));
  }, [uid]);

  useEffect(() => {
    if (tab !== 'achievements' || earned) return;
    getDocs(query(collection(db, 'user_achievements'), where('userId', '==', uid), limit(100)))
      .then((s) => {
        const m: Record<string, Date | null> = {};
        s.docs.forEach((d) => { const v = d.data(); m[String(v.badgeId ?? '')] = toDate(v.earnedAt); });
        setEarned(m);
      })
      .catch(() => setEarned({}));
  }, [tab, uid, earned]);

  const loadFriends = useCallback(async () => {
    const people = new Map<string, AdminUser | null>();
    const person = async (id: string) => {
      if (!people.has(id)) {
        const s = await getDoc(doc(db, 'users', id)).catch(() => null);
        people.set(id, s && s.exists() ? normalizeUser(s.id, s.data()) : null);
      }
      return people.get(id) ?? null;
    };
    const row = async (key: string, otherId: string, fallback: string, docId: string, coll: FriendRow['coll']): Promise<FriendRow> => {
      const p = await person(otherId);
      return {
        key, uid: otherId, docId, coll,
        name: p?.username || p?.displayName || fallback || 'Ismeretlen',
        sub: [p ? `CP ${fmtInt(p.dqScore)}` : '', p?.city].filter(Boolean).join(' · '),
        avatar: p?.avatarUrl ?? null,
      };
    };
    try {
      const [f1, f2, inc, out] = await Promise.all([
        getDocs(query(collection(db, 'friendships'), where('userId1', '==', uid), limit(100))),
        getDocs(query(collection(db, 'friendships'), where('userId2', '==', uid), limit(100))),
        getDocs(query(collection(db, 'friend_requests'), where('toUserId', '==', uid), limit(50))),
        getDocs(query(collection(db, 'friend_requests'), where('fromUserId', '==', uid), limit(50))),
      ]);
      const fr = await Promise.all([...f1.docs, ...f2.docs]
        .filter((d) => (d.data().status ?? 'active') === 'active')
        .slice(0, 60)
        .map((d) => {
          const v = d.data();
          const mine = v.userId1 === uid;
          return row(d.id, mine ? v.userId2 : v.userId1, mine ? v.username2 : v.username1, d.id, 'friendships');
        }));
      const ic = await Promise.all(inc.docs.filter((d) => d.data().status === 'pending')
        .map((d) => row(d.id, d.data().fromUserId, d.data().fromUsername, d.id, 'friend_requests')));
      const og = await Promise.all(out.docs.filter((d) => d.data().status === 'pending')
        .map((d) => row(d.id, d.data().toUserId, d.data().toUsername, d.id, 'friend_requests')));
      setFriends({ friends: fr, incoming: ic, outgoing: og });
    } catch (e) {
      console.warn('[profil] barátok', e);
      setFriends({ friends: [], incoming: [], outgoing: [] });
    }
  }, [uid]);
  useEffect(() => { if (tab === 'friends' && !friends) loadFriends(); }, [tab, friends, loadFriends]);

  useEffect(() => {
    if (!u?.venueIds.length) return;
    Promise.all(u.venueIds.map((id) => getDoc(doc(db, 'venues', id)).catch(() => null))).then((ss) => {
      const m: Record<string, string> = {};
      ss.forEach((s, i) => { m[u.venueIds[i]] = s && s.exists() ? String(s.data().name ?? '') : ''; });
      setVenueNames(m);
    });
  }, [u?.venueIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const record = useMemo(() => {
    const wins = u?.wins ?? 0;
    const losses = u?.losses ?? 0;
    const total = wins + losses;
    return { wins, losses, rate: total ? Math.round((wins / total) * 100) : 0 };
  }, [u]);

  if (u === undefined) return <Spinner />;
  if (u === null) {
    return (
      <div>
        <PageHeader title="Játékos profil" right={<Button kind="ghost" icon="ArrowLeft" onClick={() => nav('/users')}>Vissza a listához</Button>} />
        <Card><Empty>Ez a felhasználó nem található.</Empty></Card>
      </div>
    );
  }

  const role = roleOf(u);
  const active = isActive(u);

  return (
    <div>
      <PageHeader
        title="Játékos profil"
        sub={<><button type="button" className="hover:text-white" onClick={() => nav('/users')}>Felhasználók</button> › {u.displayName}</>}
        right={<>
          <Button kind="ghost" icon="ArrowLeft" onClick={() => nav('/users')}>Vissza a listához</Button>
          <Button icon="PencilSimpleLine" onClick={() => setDialog({ kind: 'edit', user: u })}>Szerkesztés</Button>
        </>}
      />

      {/* fejléc-kártya */}
      <Card className="!p-6 flex items-center gap-6 flex-wrap">
        <Avatar src={u.avatarUrl} name={u.displayName} size={88} />
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="text-[22px] font-bold text-white">{u.displayName}</span>
            <Badge tone={role === 'user' ? 'cyan' : ROLE_TONE[role]}>{role === 'user' ? 'Játékos' : ROLE_LABEL[role]}</Badge>
            <Badge tone={active ? 'green' : 'gray'}>{active ? 'Aktív' : 'Inaktív'}</Badge>
            {u.isPremium && <Badge tone="gold">Prémium</Badge>}
          </div>
          <div className="mt-2 text-[13px] text-[#B8B8C3]">
            {[u.username && `@${u.username}`, u.email, u.city].filter(Boolean).join(' · ')}
          </div>
          <div className="mt-1 text-[12px] text-[#8a8f99]">
            Regisztrált: {fmtDateDot(u.createdAt)} · Utolsó aktivitás: {lastActiveText(u.lastActive)}
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          {[
            { v: fmtInt(record.wins), l: 'Győzelem', c: '#2bc48a' },
            { v: fmtInt(record.losses), l: 'Vereség', c: '#ef4444' },
            { v: `${record.rate}%`, l: 'Győzelmi arány', c: '#00AFF2' },
            { v: fmtInt(u.dqScore), l: 'CP', c: '#d9a514' },
          ].map((s) => (
            <div key={s.l} className="min-w-[78px] px-4 py-2.5 rounded-xl bg-[#1d232a] text-center">
              <div className="text-[22px] font-bold leading-7" style={{ color: s.c }}>{s.v}</div>
              <div className="text-[11px] text-[#8a8f99] mt-0.5">{s.l}</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="mt-6">
        <Tabs<Tab> value={tab} onChange={setTab} options={[
          { value: 'stats', label: 'Statisztikák' },
          { value: 'matches', label: 'Mérkőzés történet' },
          { value: 'achievements', label: 'Eredmények' },
          { value: 'friends', label: 'Barátok' },
          { value: 'operation', label: 'Üzemeltetés' },
          { value: 'settings', label: 'Beállítások' },
        ]} />
      </div>

      {tab === 'stats' && <StatsTab u={u} xp={xp} />}
      {tab === 'matches' && <MatchesTab rows={matches} />}
      {tab === 'achievements' && <AchievementsTab earned={earned} />}
      {tab === 'friends' && (
        <FriendsTab data={friends} onOpen={(id) => nav(`/users/${id}`)} onRemove={async (r) => {
          try {
            await updateDoc(doc(db, r.coll, r.docId), { status: r.coll === 'friendships' ? 'removed' : 'declined' });
            toast(r.coll === 'friendships' ? 'Barátság megszüntetve.' : 'Kérelem törölve.');
            loadFriends();
          } catch {
            toast('A művelet nem sikerült.', 'red');
          }
        }} />
      )}
      {tab === 'operation' && (
        <Card className="!p-6">
          <SectionTitle right={<Button kind="ghost" icon="MapPin" onClick={() => setDialog({ kind: 'venue', user: u })}>Helyszín hozzárendelése</Button>}>
            Üzemeltetett helyszínek
          </SectionTitle>
          {u.isVenueOperator && u.venueIds.length ? (
            <div className="flex flex-col gap-2">
              {u.venueIds.map((id) => (
                <div key={id} className="flex items-center gap-3 h-[48px] px-4 rounded-xl bg-[#1d232a]">
                  <Icon name="MapPin" size={18} className="text-[#d9a514]" />
                  <span className="text-[14px] text-white">{venueNames[id] || id}</span>
                </div>
              ))}
            </div>
          ) : (
            <Empty>{u.isVenueOperator ? 'Üzemeltető, de még nincs hozzárendelt helyszíne.' : 'Nem üzemeltető. A „Helyszín hozzárendelése” gombbal azzá teheted.'}</Empty>
          )}
        </Card>
      )}
      {tab === 'settings' && (
        <SettingsTab u={u} onDialog={setDialog} onPremium={async (v) => {
          try { await updateDoc(doc(db, 'users', u.uid), { isPremium: v }); toast(v ? 'Prémium bekapcsolva.' : 'Prémium kikapcsolva.'); } catch { toast('A mentés nem sikerült.', 'red'); }
        }} />
      )}

      <UserDialogs
        dialog={dialog}
        close={() => setDialog(null)}
        reload={() => undefined}
        selfUid={me?.uid}
        onDeleted={() => nav('/users')}
      />
    </div>
  );
}

/* ---------- Statisztikák (ADM_02e) ---------- */
export function StatsTab({ u, xp }: { u: AdminUser; xp: XpRow[] | null }) {
  const next = (Math.floor(u.totalXP / 1000) + 1) * 1000;
  const pct = Math.min(100, ((u.totalXP % 1000) / 1000) * 100);
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon="Target" tone="cyan" label="Összes mérkőzés" value={fmtInt(u.totalMatches)} />
        <StatCard icon="ChartBar" tone="purple" label="Átlagos pontszám" value={u.averagePPR ? u.averagePPR.toFixed(1).replace('.', ',') : '—'} valueColor="#a855f7" />
        <StatCard icon="Trophy" tone="gold" label="Legjobb finish" value={u.highestCheckout ? fmtInt(u.highestCheckout) : '—'} valueColor="#d9a514" />
        <StatCard icon="Lightning" tone="green" label="180-as dobások" value={fmtInt(u.total180s)} valueColor="#2bc48a" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_400px] gap-4 mt-4">
        <Card className="!p-5">
          <SectionTitle right={<Badge tone="cyan">{u.dqLevel}. szint</Badge>}>Szint előrehaladás</SectionTitle>
          <div className="h-2 rounded-full bg-[#31383D] overflow-hidden">
            <div className="h-full rounded-full bg-[#00AFF2]" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-2.5 text-[12px] text-[#8a8f99]">
            {fmtInt(u.totalXP)} / {fmtInt(next)} XP · {fmtInt(next - u.totalXP)} XP a következő szintig
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="p-3.5 rounded-xl bg-[#1d232a]">
              <div className="text-[12px] text-[#8a8f99]">Checkout arány</div>
              <div className="text-[20px] font-bold text-white mt-0.5">{Math.round(u.checkoutPercentage)}%</div>
            </div>
            <div className="p-3.5 rounded-xl bg-[#1d232a]">
              <div className="text-[12px] text-[#8a8f99]">Elkölthető XP</div>
              <div className="text-[20px] font-bold text-white mt-0.5">{fmtInt(u.currentXP)}</div>
            </div>
          </div>
        </Card>
        <Card className="!p-5">
          <SectionTitle>Legutóbbi XP szerzések</SectionTitle>
          {xp === null ? <Spinner /> : xp.length === 0 ? <Empty>Még nincs XP-mozgás.</Empty> : xp.slice(0, 5).map((x, i, arr) => (
            <div key={x.id} className={`flex items-center justify-between gap-3 py-2.5 ${i < arr.length - 1 ? 'border-b border-[#31383D]' : ''}`}>
              <span className="text-[13px] text-white truncate">{x.label}</span>
              <span className="text-[13px] font-semibold whitespace-nowrap" style={{ color: x.amount >= 0 ? '#2bc48a' : '#ef4444' }}>
                {x.amount >= 0 ? '+' : '−'}{fmtInt(Math.abs(x.amount))} XP
              </span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

/* ---------- Mérkőzés történet (ADM_02f) ---------- */
function MatchesTab({ rows }: { rows: MatchRow[] | null }) {
  const [shown, setShown] = useState(25);
  const RES: Record<MatchRow['result'], { t: string; tone: Tone }> = {
    win: { t: 'Győzelem', tone: 'green' }, loss: { t: 'Vereség', tone: 'red' }, draw: { t: 'Döntetlen', tone: 'gold' },
  };
  return (
    <div className="bg-[#15191E] border border-[#31383D] rounded-2xl overflow-hidden">
      {rows === null ? <Spinner /> : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left">
            <thead>
              <tr className="bg-[#1d232a] text-[12px] uppercase text-[#8a8f99]">
                <th className="pl-5 pr-4 h-[39px] font-semibold w-[26%]">Ellenfél</th>
                <th className="px-4 font-semibold w-[14%]">Eredmény</th>
                <th className="px-4 font-semibold w-[13%]">Pontszám</th>
                <th className="px-4 font-semibold w-[14%]">Játékmód</th>
                <th className="px-4 font-semibold w-[20%]">Helyszín</th>
                <th className="px-4 font-semibold w-[13%]">Dátum</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, shown).map((m) => (
                <tr key={m.id} className="border-t border-[#31383D] h-[63px]">
                  <td className="pl-5 pr-4">
                    <div className="flex items-center gap-2.5">
                      <Avatar name={m.opponent} size={28} />
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold text-white truncate">{m.opponent}</div>
                        {m.opponentSub && <div className="text-[12px] text-[#8a8f99]">{m.opponentSub}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4"><Badge tone={RES[m.result].tone}>{RES[m.result].t}</Badge></td>
                  <td className="px-4 text-[14px] text-[#B8B8C3]">{m.score}</td>
                  <td className="px-4 text-[14px] text-[#B8B8C3]">{m.mode}</td>
                  <td className="px-4 text-[14px] text-[#B8B8C3] truncate max-w-[220px]">{m.venue}</td>
                  <td className="px-4 text-[14px] text-[#B8B8C3]">{fmtDateDot(m.date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <Empty>Még nincs lejátszott mérkőzés.</Empty>}
          {rows.length > shown && (
            <div className="border-t border-[#31383D] p-4 flex justify-end">
              <Button kind="ghost" onClick={() => setShown((s) => s + 25)}>Továbbiak betöltése</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Eredmények (ADM_02g) ---------- */
export function AchievementsTab({ earned }: { earned: Record<string, Date | null> | null }) {
  const [sort, setSort] = useState<'earned' | 'locked' | 'name' | 'rarity'>('earned');
  const [cat, setCat] = useState<'all' | Cat>('all');
  if (!earned) return <Spinner />;
  const list = BADGES
    .filter((b) => cat === 'all' || b.cat === cat)
    .map((b) => ({ ...b, on: b.id in earned, at: earned[b.id] ?? null }))
    .sort((a, b) => {
      if (sort === 'name') return a.title.localeCompare(b.title, 'hu');
      if (sort === 'rarity') return TIER_RANK[b.tier] - TIER_RANK[a.tier];
      const d = Number(b.on) - Number(a.on);
      return sort === 'earned' ? d : -d;
    });
  return (
    <div>
      <div className="flex items-center gap-x-6 gap-y-3 flex-wrap mb-5">
        <div className="flex items-center gap-2.5">
          <span className="text-[13px] text-[#8a8f99]">Rendezés:</span>
          <Segmented value={sort} onChange={setSort} options={[
            { value: 'earned', label: 'Elért felül' }, { value: 'locked', label: 'Elért alul' },
            { value: 'name', label: 'Név' }, { value: 'rarity', label: 'Ritkaság' },
          ]} />
        </div>
        <div className="flex items-center gap-2.5">
          <span className="text-[13px] text-[#8a8f99]">Kategória:</span>
          <Segmented value={cat} onChange={setCat} options={[
            { value: 'all', label: 'Összes' }, { value: 'wins', label: 'Győzelmek' }, { value: 'matches', label: 'Mérkőzések' },
            { value: 'skill', label: 'Ügyesség' }, { value: 'other', label: 'Egyéb' },
          ]} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {list.map((b) => (
          <Card key={b.id} className={`!p-5 ${b.on ? '' : 'opacity-[0.45]'}`}>
            <div className="flex items-start gap-3">
              <IconTile icon={b.icon} tone={TIER_TONE[b.tier]} size={44} iconSize={24} />
              <div className="min-w-0">
                <div className={`text-[14px] font-semibold ${b.on ? 'text-white' : 'text-[#8a8f99]'}`}>{b.title}</div>
                <div className="text-[12px] text-[#8a8f99] mt-0.5">{b.desc}</div>
              </div>
            </div>
            <div className="mt-3" title={b.at ? `Feloldva: ${fmtDateDot(b.at)}` : undefined}>
              <Badge tone={b.on ? 'green' : 'gray'}>{b.on ? 'Feloldva' : 'Zárva'}</Badge>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------- Barátok (ADM_02h) ---------- */
function FriendsTab({ data, onOpen, onRemove }: {
  data: { friends: FriendRow[]; incoming: FriendRow[]; outgoing: FriendRow[] } | null;
  onOpen: (uid: string) => void; onRemove: (r: FriendRow) => void;
}) {
  const [sub, setSub] = useState<'friends' | 'incoming' | 'outgoing'>('friends');
  if (!data) return <Spinner />;
  const rows = data[sub];
  return (
    <div>
      <div className="mb-5">
        <Segmented value={sub} onChange={setSub} options={[
          { value: 'friends', label: `Barátok (${data.friends.length})` },
          { value: 'incoming', label: `Bejövő (${data.incoming.length})` },
          { value: 'outgoing', label: `Kimenő (${data.outgoing.length})` },
        ]} />
      </div>
      {rows.length === 0 ? <Card><Empty>Itt még nincs senki.</Empty></Card> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((r) => (
            <Card key={r.key} className="!p-4 flex items-center gap-3">
              <Avatar src={r.avatar} name={r.name} size={40} />
              <button type="button" onClick={() => onOpen(r.uid)} className="flex-1 min-w-0 text-left">
                <div className="text-[14px] font-semibold text-white truncate">{r.name}</div>
                <div className="text-[12px] text-[#8a8f99] truncate">{r.sub || '—'}</div>
              </button>
              <ActionMenu trigger="DotsThree" items={[
                { icon: 'UserCircle', label: 'Profil megnyitása', onClick: () => onOpen(r.uid) },
                {
                  icon: 'UserMinus', danger: true,
                  label: sub === 'friends' ? 'Barátság megszüntetése' : 'Kérelem törlése',
                  onClick: () => onRemove(r),
                },
              ]} />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Beállítások ---------- */
function SettingsTab({ u, onDialog, onPremium }: { u: AdminUser; onDialog: (d: Dialog) => void; onPremium: (v: boolean) => void }) {
  const toast = useToast();
  const Row = ({ title, sub, children }: { title: string; sub: string; children: React.ReactNode }) => (
    <div className="flex items-center justify-between gap-4 py-4 border-b border-[#31383D] last:border-0">
      <div>
        <div className="text-[14px] font-semibold text-white">{title}</div>
        <div className="text-[12px] text-[#8a8f99] mt-0.5">{sub}</div>
      </div>
      {children}
    </div>
  );
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_400px] gap-4">
      <Card className="!px-5 !py-1">
        <Row title="Admin jogosultság" sub="Teljes hozzáférés az admin felülethez">
          <Toggle checked={u.isAdmin} onChange={() => onDialog({ kind: 'admin', user: u })} />
        </Row>
        <Row title="Üzemeltető" sub="Helyszínek kezelése a klub panelen">
          <Toggle checked={u.isVenueOperator} onChange={() => onDialog({ kind: u.isVenueOperator ? 'operatorOff' : 'venue', user: u })} />
        </Row>
        <Row title="Edző" sub="Edzői profil és edzések hirdetése">
          <Toggle checked={u.isCoach} onChange={() => onDialog({ kind: 'coach', user: u })} />
        </Row>
        <Row title="Prémium fiók" sub="Prémium funkciók a mobilappban">
          <Toggle checked={u.isPremium} onChange={onPremium} />
        </Row>
      </Card>
      <div className="flex flex-col gap-4">
        <Card className="!p-5">
          <SectionTitle>Azonosító</SectionTitle>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 truncate px-3 h-[38px] leading-[38px] rounded-lg bg-[#1d232a] text-[12px] text-[#B8B8C3]">{u.uid}</code>
            <Button kind="ghost" onClick={() => { navigator.clipboard?.writeText(u.uid); toast('Azonosító kimásolva.'); }}>Másolás</Button>
          </div>
        </Card>
        <Card className="!p-5 !border-[#ef4444]/40">
          <SectionTitle>Veszélyzóna</SectionTitle>
          <p className="text-[13px] text-[#B8B8C3] mb-4">A felhasználói dokumentum törlése nem vonható vissza.</p>
          <Button kind="danger" icon="Trash" onClick={() => onDialog({ kind: 'delete', user: u })}>Felhasználó törlése</Button>
        </Card>
      </div>
    </div>
  );
}
